/**
 * Rango de fechas del resumen (feedback de la reunión del 19/08).
 *
 * Está afuera del componente para poder testear las fechas sin renderizar nada:
 * es la parte que se rompe en silencio, no el select.
 *
 * HORA ARGENTINA, siempre. El backend traduce "2026-08-19" a medianoche
 * argentina (api/src/hce-summary/range.js); si acá "hoy" se calculara con la
 * hora local del navegador, un usuario con la máquina en otro huso — o cualquiera
 * después de las 21:00 hora argentina, cuando en UTC ya es el día siguiente —
 * pediría un día distinto del que ve en pantalla. Es exactamente el bug que ya
 * pasó con el odontograma: e2e verde a las 16hs, rojo a las 23hs.
 */

const AR_TZ = 'America/Argentina/Buenos_Aires'

// 'fr-ca' da YYYY-MM-DD, que es el formato que espera el backend. Mismo truco
// que usa api/src/arca/emit.js para la fecha de comprobante.
const arTodayParts = (now = new Date()) => {
  const [year, month, day] = new Intl.DateTimeFormat('fr-ca', { timeZone: AR_TZ })
    .format(now)
    .split('-')
    .map(Number)
  return { year, month, day }
}

const pad = n => String(n).padStart(2, '0')
const toISO = ({ year, month, day }) => `${year}-${pad(month)}-${pad(day)}`

export const arToday = (now = new Date()) => toISO(arTodayParts(now))

// Restar meses de calendario con Date.UTC sobre los COMPONENTES del día
// argentino (no sobre el instante): así "hoy menos 3 meses" es el mismo día de
// otro mes y no se corre por husos ni por horario de verano de otro país.
//
// El clamp de fin de mes es real, no teórico: 31 de marzo menos 1 mes daría 31
// de febrero, que Date convierte en marzo sin avisar — el rango arrancaría
// DESPUÉS de lo pedido.
export const arDayMinusMonths = (months, now = new Date()) => {
  const { year, month, day } = arTodayParts(now)
  const target = new Date(Date.UTC(year, month - 1 - months, 1))
  const targetYear = target.getUTCFullYear()
  const targetMonth = target.getUTCMonth() + 1
  const lastDay = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate()
  return toISO({ year: targetYear, month: targetMonth, day: Math.min(day, lastDay) })
}

/**
 * Presets en vez de sólo desde/hasta libre (decisión de Nacho, 19/08, opción 3C).
 * El motivo no es la UI: cada par de fechas distinto es una pregunta distinta
 * para el cooldown, así que con rangos libres el reuso se cae a casi cero y el
 * análisis de costo queda ilegible (mil rangos únicos, ninguno comparable).
 * Los presets acotan eso; "personalizado" queda para el caso puntual.
 *
 * 'all' primero y como default: para quien no toca nada, no cambia nada.
 */
export const RANGE_PRESETS = [
  { key: 'all', label: 'Toda la historia' },
  { key: '12m', label: 'Último año', months: 12 },
  { key: '6m', label: 'Últimos 6 meses', months: 6 },
  { key: '3m', label: 'Últimos 3 meses', months: 3 },
  { key: 'custom', label: 'Personalizado' },
]

export const DEFAULT_RANGE_PRESET = 'all'

/**
 * Traduce la selección de la UI al `{from, to}` que viaja al backend.
 * `null` en un borde = sin límite de ese lado.
 */
export const resolveRange = ({ preset, customFrom, customTo }, now = new Date()) => {
  if (preset === 'custom') {
    return { from: customFrom || null, to: customTo || null }
  }
  const found = RANGE_PRESETS.find(p => p.key === preset)
  if (!found || !found.months) return { from: null, to: null }
  return { from: arDayMinusMonths(found.months, now), to: arToday(now) }
}

// Dos rangos son "el mismo pedido" si sus dos bordes coinciden. Se usa para
// avisar antes de gastar una corrida (decisión 1C): si el resumen que ya está en
// pantalla cubre otro rango, generar con el rango elegido es una corrida nueva.
export const sameRange = (a, b) => (a?.from || null) === (b?.from || null) && (a?.to || null) === (b?.to || null)

// El backend guarda los bordes en milisegundos; para comparar contra lo que
// eligió el usuario hay que volver al día argentino.
export const msToArDay = ms => (ms == null ? null : new Intl.DateTimeFormat('fr-ca', { timeZone: AR_TZ }).format(new Date(ms)))

/**
 * Qué opción del selector corresponde a un rango ya generado.
 *
 * Existe para que el panel abra COHERENTE con el resumen que muestra: si la
 * corrida en pantalla cubre 3 meses y el selector arranca en "toda la historia",
 * el aviso de "esto va a gastar una corrida" salta al abrir, antes de que el
 * profesional elija nada — puro ruido. Y suprimir el aviso sin más sería peor:
 * con el selector en "toda la historia" y un resumen de 3 meses a la vista,
 * apretar "generar de nuevo" gasta una corrida en silencio, que es exactamente
 * el caso que el aviso existe para cubrir.
 *
 * Se intenta primero con los presets (así elegir "últimos 3 meses" y generar no
 * convierte la selección en "personalizado" al volver), y si no matchea ninguno
 * cae a personalizado con las fechas de la corrida.
 */
export const presetForRange = (range, now = new Date()) => {
  const from = range?.from || null
  const to = range?.to || null
  if (from == null && to == null) return { preset: 'all', customFrom: '', customTo: '' }
  for (const option of RANGE_PRESETS) {
    if (!option.months) continue
    if (sameRange(resolveRange({ preset: option.key }, now), { from, to })) {
      return { preset: option.key, customFrom: '', customTo: '' }
    }
  }
  return { preset: 'custom', customFrom: from || '', customTo: to || '' }
}

/**
 * El período de una corrida, en texto.
 *
 * Existe porque estaba escrito DOS veces — una para la corrida actual y otra
 * para el resumen anterior — y las dos versiones ya habían divergido: una
 * formateaba las fechas ("01-mar-2026") y la otra mostraba el ISO crudo
 * ("2026-03-01"), en la misma pantalla. Además ninguna estaba cubierta por un
 * test, porque el mock de i18n devuelve la key y la interpolación no se ve.
 *
 * `formatDay` se inyecta para poder testear esto sin i18n ni locales.
 *
 * "en adelante" y no "hoy" para el tope abierto: para una corrida de la semana
 * pasada, "hoy" es una fecha que esa corrida nunca vio. Un tope abierto significa
 * "sin límite superior", y eso no envejece.
 */
export const formatRangePeriod = ({ fromMs, toMs }, { formatDay, labels = {} }) => {
  const { all = 'toda la historia', openStart = 'inicio', openEnd = 'en adelante' } = labels
  if (fromMs == null && toMs == null) return all
  const from = fromMs != null ? formatDay(fromMs) : openStart
  const to = toMs != null ? formatDay(toMs) : openEnd
  return `${from} → ${to}`
}
